import { Button } from '@finn/design-system/atoms/button';
import { If } from '@finn/design-system/atoms/If';
import { useMediaQuery } from '@finn/design-system/helpers/media';
import { InfoFilled } from '@finn/design-system/icons/info-filled';
import {
  BannerModule,
  BannerModuleDescription,
  BannerModuleTitle,
} from '@finn/design-system/modules/BannerModule';
import { cn, parseToHtml } from '@finn/ui-utils';
import { ElementType } from 'react';

type InfoboxProps = {
  title?: string;
  description?: string;
  cta_text?: string;
  cta_href?: string;
  variant?: string;
  move_up?: string;
  move_down?: string;
  heading?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  heading_style?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  heading_size?: string;
};

export const useNormalizeMargins = ({
  move_up,
  move_down,
}: {
  move_up?: string;
  move_down?: string;
}) => {
  const isMobile = useMediaQuery('md');

  // we normalize the margins for mobile, as they were defined for desktop
  // 6 is mobile padding top between modules, 14 is desktop padding top between modules
  const multiplier = isMobile ? 6 / 14 : 1;

  return {
    marginTop: move_up ? parseInt(move_up) * multiplier : undefined,
    marginBottom: move_down ? parseInt(move_down) * multiplier : undefined,
  };
};

export const Infobox = ({
  title,
  description,
  variant,
  cta_text,
  cta_href,
  heading = 'h4',
  heading_style,
  heading_size = '1.125rem',
  move_up,
  move_down,
}: InfoboxProps) => {
  const { marginTop, marginBottom } = useNormalizeMargins({
    move_up,
    move_down,
  });

  const HTag = heading.toLowerCase() as ElementType;

  return (
    <BannerModule
      className={cn(
        'container sm:w-[calc(100%-2rem)] md:w-[calc(100%-1rem)] md:max-w-[726px] lg:relative',
        variant === 'dark' && '!bg-black'
      )}
      style={{
        marginTop,
        marginBottom,
      }}
    >
      <If condition={Boolean(title)}>
        <BannerModuleTitle
          asChild
          className={cn(
            'flex items-center gap-2 before:hidden after:hidden',
            variant === 'dark' ? 'text-white' : 'text-black',
            heading_style === 'h1' && 'mobile-t1-semibold md:web-t1-semibold',
            heading_style === 'h2' && 'mobile-t2-semibold md:web-t2-semibold',
            heading_style === 'h3' && 'mobile-t3-semibold md:web-t3-semibold',
            heading_style === 'h4' && 'mobile-t4-semibold md:web-t4-semibold',
            heading_style === 'h5' && 'global-t5-semibold',
            heading_style === 'h6' && 'global-t6-semibold'
          )}
        >
          <HTag style={heading_style ? {} : { fontSize: heading_size }}>
            <InfoFilled
              className={variant === 'dark' ? 'fill-white' : 'fill-black'}
            />
            {parseToHtml(title)}
          </HTag>
        </BannerModuleTitle>
      </If>
      <If condition={Boolean(description)}>
        <BannerModuleDescription
          className={cn(
            'raw-html-global !body-16-light',
            variant === 'dark' ? 'text-white' : 'text-black'
          )}
        >
          {parseToHtml(description)}
        </BannerModuleDescription>
      </If>
      <If condition={Boolean(cta_text && cta_href)}>
        <Button href={cta_href}>{cta_text}</Button>
      </If>
    </BannerModule>
  );
};
